import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import {
  Building2, LayoutDashboard, CreditCard, Users, Shield, LogOut, ChevronLeft, Briefcase, Receipt, Megaphone, Headset, PhoneCall, TicketPercent, Settings, Activity, Menu
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import RealtimeStatusBadge from "@/components/RealtimeStatusBadge";
import MobileBottomNav from "@/components/MobileBottomNav";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useGlobalRealtimeSync } from "@/hooks/useGlobalRealtimeSync";
import SubscriptionAlertsBanner from "@/components/SubscriptionAlertsBanner";
import NurseryProfileCompletionBanner from "@/components/NurseryProfileCompletionBanner";

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "لوحة التحكم", end: true },
  { to: "/admin/activity", icon: Activity, label: "نشاط الحضانات" },
  { to: "/admin/nurseries", icon: Building2, label: "إدارة الحضانات" },
  { to: "/admin/subscriptions", icon: CreditCard, label: "الاشتراكات والدفع" },
  { to: "/admin/promo-codes", icon: TicketPercent, label: "الأكواد الترويجية" },
  { to: "/admin/staff", icon: Briefcase, label: "الموظفون" },
  { to: "/admin/billing", icon: Receipt, label: "الحسابات" },
  { to: "/admin/users", icon: Users, label: "المستخدمون" },
  { to: "/admin/permissions", icon: Shield, label: "الصلاحيات" },
  { to: "/admin/broadcast", icon: Megaphone, label: "إشعار عام" },
  { to: "/admin/support", icon: Headset, label: "الدعم الفني" },
  { to: "/admin/settings", icon: Settings, label: "إعدادات النظام" },
  { to: "/admin/system-test", icon: Activity, label: "فحص النظام" },
];

const SidebarBody = ({ user, signOut, navigate, onNavigate }: any) => (
  <>
    <div className="p-4 border-b border-slate-700">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-400" />
          لوحة المشرف العام
        </h1>
        <NotificationBell />
      </div>
      <p className="text-xs text-slate-400 mt-1 truncate">{user?.email}</p>
    </div>

    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
              isActive
                ? "bg-amber-500/20 text-amber-300 font-medium"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            )
          }
        >
          <item.icon className="w-4 h-4" />
          {item.label}
        </NavLink>
      ))}
    </nav>

    <div className="p-3 border-t border-slate-700 space-y-2">
      <Button
        variant="ghost"
        className="w-full justify-start gap-2 text-teal-400 hover:text-teal-300 hover:bg-teal-900/20"
        onClick={() => { navigate("/support-panel"); onNavigate?.(); }}
      >
        <PhoneCall className="w-4 h-4" />
        لوحة الدعم الفني
      </Button>
      <Button
        variant="ghost"
        className="w-full justify-start gap-2 text-orange-400 hover:text-orange-300 hover:bg-orange-900/20"
        onClick={() => { navigate("/accountant"); onNavigate?.(); }}
      >
        <Receipt className="w-4 h-4" />
        لوحة المحاسب
      </Button>
      <Button
        variant="ghost"
        className="w-full justify-start gap-2 text-slate-300 hover:text-white hover:bg-slate-800"
        onClick={() => { navigate("/dashboard"); onNavigate?.(); }}
      >
        <ChevronLeft className="w-4 h-4" />
        لوحة الحضانة
      </Button>
      <Button
        variant="ghost"
        className="w-full justify-start gap-2 text-red-400 hover:text-red-300 hover:bg-red-900/20"
        onClick={signOut}
      >
        <LogOut className="w-4 h-4" />
        تسجيل خروج
      </Button>
    </div>
  </>
);

const AdminLayout = () => {
  const { roles, loading, user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useGlobalRealtimeSync();

  const isSuperAdmin = roles.includes("super_admin");

  useEffect(() => {
    if (!loading && !isSuperAdmin) {
      navigate("/dashboard");
    }
  }, [loading, isSuperAdmin, navigate]);

  if (loading || !isSuperAdmin) return null;

  return (
    <div className="min-h-screen flex" dir="rtl">
      {/* Desktop Sidebar */}
      <aside className="w-64 bg-slate-900 text-white hidden md:flex flex-col">
        <SidebarBody user={user} signOut={signOut} navigate={navigate} />
      </aside>

      {/* Main */}
      <main className="flex-1 bg-background overflow-auto min-w-0">
        <header className="flex items-center justify-between gap-2 px-3 sm:px-6 py-3 border-b bg-card sticky top-0 z-20">
          {/* Mobile menu trigger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label="القائمة">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="p-0 w-72 bg-slate-900 text-white border-slate-700">
              <div className="flex flex-col h-full">
                <SidebarBody user={user} signOut={signOut} navigate={navigate} onNavigate={() => setOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>

          <h2 className="md:hidden text-sm font-semibold flex-1 text-center">المشرف العام</h2>

          <div className="flex items-center gap-2">
            <RealtimeStatusBadge />
          </div>
        </header>
        <div className="app-main p-4 sm:p-6 has-bottom-nav md:!pb-6">
          <NurseryProfileCompletionBanner />
          <SubscriptionAlertsBanner linkTo="/accountant/monthly-status" />
          <Outlet />
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default AdminLayout;
